import { Skeleton } from "@/components/ui/skeleton";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * Thin indeterminate progress bar — used at the top of a content area while
 * data is loading. Pairs with skeleton placeholders below for a Gemini-style
 * "page chrome stays, data fills in progressively" loading UX.
 */
export const TopLoadingBar = ({ className }: { className?: string }) => (
  <div
    role="progressbar"
    aria-busy="true"
    aria-label="Loading"
    className={cn(
      "relative h-0.5 w-full overflow-hidden rounded-full bg-primary/10",
      className,
    )}
  >
    <div className="absolute inset-y-0 left-0 w-1/4 animate-indeterminate-bar rounded-full bg-primary" />
  </div>
);

/**
 * Single low-contrast skeleton card that mirrors the StaffInsights MetricPanel
 * shape — icon chip + label + value + caption. Kept subtle so it reads as
 * structure, not as fake content waiting to be replaced.
 */
export const MetricPanelSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex h-[160px] flex-col rounded-xl border border-border/40 bg-background/60 p-5",
      className,
    )}
    aria-hidden="true"
  >
    <div className="flex items-start gap-2.5">
      <Skeleton className="h-7 w-7 rounded-md opacity-60" />
      <Skeleton className="h-3 w-28 rounded opacity-60" />
    </div>
    <div className="mt-auto space-y-2">
      <Skeleton className="h-7 w-16 rounded opacity-60" />
      <Skeleton className="h-2.5 w-24 rounded opacity-60" />
    </div>
  </div>
);

export const MetricsGridSkeleton = ({
  count = 6,
  className,
}: {
  count?: number;
  className?: string;
}) => (
  <section
    className={cn(
      "grid gap-3 md:grid-cols-2 xl:grid-cols-3",
      className,
    )}
    aria-busy="true"
    aria-label="Loading metrics"
  >
    {Array.from({ length: count }).map((_, i) => (
      <MetricPanelSkeleton key={i} />
    ))}
  </section>
);

/**
 * Skeleton row used inside a Table — preserves the column structure so the
 * table's column widths stay stable when real rows arrive.
 */
export const TableRowSkeleton = ({
  columns,
  className,
}: {
  columns: number;
  className?: string;
}) => (
  <TableRow className={cn("border-border/40 hover:bg-transparent", className)} aria-hidden="true">
    {Array.from({ length: columns }).map((_, i) => (
      <TableCell key={i} className="py-4">
        <Skeleton className="h-3 w-full max-w-[160px] rounded opacity-60" />
      </TableCell>
    ))}
  </TableRow>
);

/**
 * Inline progress-bar-only row for use inside a Table where preserving column
 * shapes isn't necessary — spans the whole row with a thin progress bar.
 */
export const TableLoadingRow = ({ colSpan }: { colSpan: number }) => (
  <TableRow className="border-border/50 hover:bg-transparent">
    <TableCell colSpan={colSpan} className="px-4 py-3">
      <TopLoadingBar />
    </TableCell>
  </TableRow>
);

/**
 * Card-shaped skeleton: title + caption + 3 body lines. Use for list views
 * where content cards have a heading + meta + description.
 */
export const CardSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "rounded-xl border border-border/40 bg-background/60 p-5",
      className,
    )}
    aria-hidden="true"
  >
    <div className="flex items-start gap-3">
      <Skeleton className="h-10 w-10 shrink-0 rounded-lg opacity-60" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-2/3 rounded opacity-60" />
        <Skeleton className="h-2.5 w-1/3 rounded opacity-60" />
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <Skeleton className="h-2.5 w-full rounded opacity-60" />
      <Skeleton className="h-2.5 w-5/6 rounded opacity-60" />
    </div>
  </div>
);

export const CardGridSkeleton = ({
  count = 6,
  className,
}: {
  count?: number;
  className?: string;
}) => (
  <section
    className={cn(
      "grid gap-3 sm:grid-cols-2 xl:grid-cols-3",
      className,
    )}
    aria-busy="true"
    aria-label="Loading cards"
  >
    {Array.from({ length: count }).map((_, i) => (
      <CardSkeleton key={i} />
    ))}
  </section>
);

/**
 * Subtle row skeleton for simple lists (chats, items, files).
 */
export const ListItemSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex items-center justify-between gap-4 border-b border-border/40 py-4",
      className,
    )}
    aria-hidden="true"
  >
    <div className="min-w-0 flex-1 space-y-1.5">
      <Skeleton className="h-3.5 w-1/2 rounded opacity-60" />
      <Skeleton className="h-2.5 w-1/4 rounded opacity-60" />
    </div>
    <Skeleton className="h-6 w-6 shrink-0 rounded opacity-60" />
  </div>
);

export const ListSkeleton = ({
  rows = 4,
  className,
}: {
  rows?: number;
  className?: string;
}) => (
  <div
    className={cn("border-t border-border/40", className)}
    aria-busy="true"
    aria-label="Loading list"
  >
    {Array.from({ length: rows }).map((_, i) => (
      <ListItemSkeleton key={i} />
    ))}
  </div>
);

/**
 * Generic detail view skeleton — header + stat strip + body block.
 * Used for single-item detail pages like Meeting, Artifact.
 */
export const DetailSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("space-y-6", className)} aria-busy="true" aria-label="Loading details">
    <div className="space-y-2">
      <Skeleton className="h-6 w-2/3 max-w-md rounded opacity-60" />
      <Skeleton className="h-3 w-1/2 max-w-sm rounded opacity-60" />
    </div>
    <div className="grid gap-3 sm:grid-cols-3">
      <Skeleton className="h-16 w-full rounded-xl opacity-60" />
      <Skeleton className="h-16 w-full rounded-xl opacity-60" />
      <Skeleton className="h-16 w-full rounded-xl opacity-60" />
    </div>
    <div className="rounded-xl border border-border/40 bg-background/60 p-6">
      <Skeleton className="h-4 w-32 rounded opacity-60" />
      <div className="mt-4 space-y-2">
        <Skeleton className="h-2.5 w-full rounded opacity-60" />
        <Skeleton className="h-2.5 w-5/6 rounded opacity-60" />
        <Skeleton className="h-2.5 w-3/4 rounded opacity-60" />
        <Skeleton className="h-2.5 w-4/5 rounded opacity-60" />
      </div>
    </div>
  </div>
);
